import { Component, EventEmitter, forwardRef, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import {
    AlainConfigService,
    AlainDateRangePickerShortcut,
    AlainDateRangePickerShortcutItem,
    deepMergeKey,
    fixEndTimeOfRange,
    getTimeDistance,
    InputBoolean,
} from '@delon/util';
import { FunctionProp, NzSafeAny } from 'ng-zorro-antd/core/types';
import { NzRangePickerComponent } from 'ng-zorro-antd/date-picker';
import * as moment from 'moment';

@Component({
    selector: 'clink-customize-range-picker',
    exportAs: 'ClinkCustomizeRangePicker',
    templateUrl: './customize-range-picker.component.html',
    styleUrls: ['./customize-range-picker.component.less'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => CustomizeRangePickerComponent),
        },
    ],
})
export class CustomizeRangePickerComponent implements ControlValueAccessor {
    selectedValue = '最近一周';
    private onChangeFn: (val: Date) => void;
    private _shortcut: AlainDateRangePickerShortcut;
    private defaultShortcuts: AlainDateRangePickerShortcut;
    convenientVisible = false;
    @ViewChild('comp', { static: false }) private comp: NzRangePickerComponent;
    value;
    @Input() public set actionTime(item: Array<any>) {
        if (item) {
            this.value = item;
        }
    }
    @Input()
    set shortcut(val: AlainDateRangePickerShortcut | null) {
        const item = deepMergeKey({}, true, this.defaultShortcuts, val == null ? {} : val) as AlainDateRangePickerShortcut;
        if (typeof val === 'boolean') {
            item.enabled = val;
        }
        (item.list || []).forEach((i) => {
            i._text = this.dom.bypassSecurityTrustHtml(i.text);
        });
        const ind = item.list.findIndex((v) => v.text === '全年');
        if (ind > -1) {
            item.list.splice(ind, 1);
        }
        this._shortcut = item;
    }
    get shortcut(): AlainDateRangePickerShortcut | null {
        return this._shortcut;
    }
    @Output() readonly ngModelEndChange = new EventEmitter<Date>();

    // #region Native properties
    @Input() nzDefaultPickerValue: Date | Date[] = getTimeDistance('today');
    @Input() nzAllowClear = true; // 是否显示清除按钮
    @Input() nzAutoFocus = false; // 自动获取焦点
    @Input() nzClassName: string; // 组件样式类名
    @Input() nzDisabled: boolean; // 禁用状态
    @Input() nzSize = 'small'; // 组件大小
    @Input() nzStyle: string; // 组件样式
    @Input() nzDisabledDate: (d: Date) => boolean; // 不可选择的日期
    @Input() nzLocale: object; // 国际化配置
    @Input() nzPopupStyle: object; // 额外的弹出日历样式
    @Input() nzDropdownClassName: string; // 额外的弹出日历 className
    @Input() nzPlaceHolder: string | string[]; // 输入框提示文字
    @Output() readonly nzOnOpenChange = new EventEmitter<boolean>(); // 弹出日历和关闭日历的回调

    // range
    @Input() nzDateRender: TemplateRef<Date> | string | ((d: Date) => TemplateRef<Date> | string); // 自定义日期单元格的内容
    @Input() nzFormat: string; // 展示的日期格式
    @Input() nzDisabledTime: (current: Date) => { nzDisabledHours; nzDisabledMinutes; nzDisabledSeconds }; // 不可选择的时间
    @Input() nzRenderExtraFooter: FunctionProp<TemplateRef<void> | string>; // 在面板中添加额外的页脚
    @Input() nzShowTime: object | boolean; // 增加时间选择功能
    @Input() @InputBoolean() nzShowToday = true; // 是否展示“今天”按钮
    @Input() nzMode: any; // 选择模式
    @Input() nzRanges: { [key: string]: Date[] } | { [key: string]: () => Date[] }; // 预设时间范围快捷选择
    @Input() convenientPicker: boolean; // 简单选择时间
    @Output() readonly nzOnPanelChange = new EventEmitter<any>(); // 面板展示/隐藏
    @Output() readonly nzOnOk = new EventEmitter<any>(); // 点击确定按钮的回调
    @Output() readonly nzModelChange = new EventEmitter<any>(); // 选中日期变更
    // #endregion
    constructor(private dom: DomSanitizer, configSrv: AlainConfigService) {
        const cog = configSrv.merge('dataRange', {
            nzFormat: 'yyyy/MM/dd',
            nzAllowClear: true,
            nzAutoFocus: false,
            nzPopupStyle: { position: 'relative' },
            nzShowToday: true,
            shortcuts: {
                enabled: false,
                closed: true,
                list: [
                    {
                        text: '今天',
                        fn: () => getTimeDistance('today'),
                    },
                    {
                        text: '昨天',
                        fn: () => getTimeDistance('yesterday'),
                    },
                    {
                        text: '本周',
                        fn: () => getTimeDistance('week'),
                    },
                    {
                        text: '本月',
                        fn: () => getTimeDistance('month'),
                    },
                    {
                        text: '最近一周',
                        fn: () => getTimeDistance(-6),
                    },
                    {
                        text: '最近一月',
                        fn: () => getTimeDistance(-29),
                    },
                    // {
                    //     text: '全年',
                    //     fn: () => getTimeDistance('year'),
                    // },
                ],
            },
        });
        if (cog) {
            this.defaultShortcuts = { ...cog.shortcuts } as AlainDateRangePickerShortcut;
            Object.assign(this, cog);
        }
    }

    _nzOnOpenChange(e: any): void {
        this.nzOnOpenChange.emit(e);
    }

    _nzOnPanelChange(e: any): void {
        this.nzOnPanelChange.emit(e);
    }

    _nzOnOk(e: any): void {
        this.nzOnOk.emit(e);
    }
    _nzModelChange(e): void {
        // nzShowTime 未定义或者值为 false 时处理数据
        if ((e && typeof this.nzShowTime === 'undefined') || this.nzShowTime === false) {
            if (e[0]) {
                // 不展示时分秒时，设置起始时间值
                const dateValue = e[0].valueOf();
                const formatValue = moment(dateValue).format('YYYY/MM/DD');
                const timestampValue = moment(`${formatValue} 00:00:00`).format('x');
                const numberValue = Number(timestampValue);
                if (numberValue) {
                    e[0] = new Date(numberValue);
                }
            }
            if (e[1]) {
                // 不展示时分秒时，设置末尾时间
                const dateValue = e[1].valueOf();
                const formatValue = moment(dateValue).format('YYYY/MM/DD');
                const timestampValue = moment(`${formatValue} 23:59:59`).format('x');
                const numberValue = Number(timestampValue);
                if (numberValue) {
                    e[1] = new Date(numberValue);
                }
            }
        }
        this.nzModelChange.emit(e);
    }
    valueChange(e: [Date, Date]): void {
        if (this.onChangeFn) {
            e = fixEndTimeOfRange(e);
            this.onChangeFn(e[0]);
            //   this.ngModelEnd = e[1];
            //   this.ngModelEndChange.emit(e[1]);
        }
    }

    writeValue(value: Date): void {
        // this.value = value && this.ngModelEnd ? [value, this.ngModelEnd] : [];
        // if (value && this.ngModelEnd) {
        // this.valueChange([value, this.ngModelEnd])
        // }
    }

    registerOnChange(fn: (val: Date) => void): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(_fn: () => void): void {
        // this.onTouchedFn = fn;
    }

    setDisabledState(disabled: boolean): void {
        this.nzDisabled = disabled;
    }

    clickShortcut(item: AlainDateRangePickerShortcutItem): void {
        this.selectedValue = item.text;
        this.value = item.fn(this.value as any);
        this._nzModelChange(this.value);
        if (this._shortcut.closed && !this.convenientPicker) {
            // tslint:disable-next-line:no-string-literal
            if ((this.comp as NzSafeAny).picker) {
                (this.comp as NzSafeAny).picker.hideOverlay();
            }
        } else if (this.convenientPicker) {
            this.convenientVisible = false;
        }
    }
}
